import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BookingQuotation } from '../booking-quotation';
import { BookingQuotationWithDetail } from '../booking-quotation-with-detail';
import { ContactRequestQuotation } from '../contact-request-quotation';
import { ContactRequestQuotationProduct } from '../contact-request-quotation-product';
import { ContactRequestQuotationWithDetail } from '../contact-request-quotation-with-detail';
import { GmailMessage } from '../gmail-message';
import { HttpService } from '../service/http.service';
import { Student } from '../student';
import { ToastMessageService } from '../toast-message.service';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-booking-quotations',
  templateUrl: './booking-quotations.component.html',
  styleUrls: ['./booking-quotations.component.css']
})
export class BookingQuotationsComponent implements OnInit {

  quotations: BookingQuotationWithDetail[] = [];
  contactRequestQuotations: ContactRequestQuotationWithDetail[] = [];

  @Input()
  bookingId!: number;

  @Input()
  student!: Student;

  @Output()
  cloningQuotationEvent = new EventEmitter<BookingQuotation>();

  displayEmailForm = false;
  displayContactRequestQuotationEmailForm = false;
  bookingQuotationEmailForm!: FormGroup | null;
  contactRequestQuotationEmailForm!: FormGroup | null;

  constructor(
    private httpService: HttpService,
    private builder: FormBuilder,
    private toastService: ToastMessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.initQuotationList();
  }

  downloadReport(quotation: BookingQuotation) {
    let languageName = this.student.language.name;
    if (languageName == null) {
      languageName = "english";
    }
    this.httpService.getBookingQuotationPdfById(quotation.id!, languageName).subscribe(blob => {
      let pdfUrl = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = pdfUrl;
      link.download = this.student.firstName + "_" + this.student.lastName + "quotation_" + languageName + quotation.id + ".pdf";
      link.click();
    });
  }

  downloadContactRequestQuotation(quotation: ContactRequestQuotation) {
    let languageName = this.student.language.name;
    if (languageName == null) {
      languageName = "english";
    }
    this.httpService.getContactRequestQuotationPdfById(quotation.id!, languageName).subscribe(blob => {
      let pdfUrl = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = pdfUrl;
      link.download = this.student.firstName + "_" + this.student.lastName + "quotation_" + languageName + quotation.id + ".pdf";
      link.click();
    });
  }

  cloneQuotation(quotation: BookingQuotation) {
    this.cloningQuotationEvent.emit(quotation);
  }

  cloneContactRequestQuotation(contactQuotation: ContactRequestQuotation) {
    let products = [];
    for (let product of contactQuotation.productList) {
      products.push(new ContactRequestQuotationProduct(
        product.productType,
        product.description,
        product.amount,
        product.currency
      ));
    }
    let quotation = new BookingQuotation(
      contactQuotation.description,
      contactQuotation.comment,
      products,
      contactQuotation.baseCurrency,
      contactQuotation.secondCurrency,
      contactQuotation.quotationDate,
      contactQuotation.dueDate,
      contactQuotation.deposit,
      contactQuotation.priceIncludes,
      contactQuotation.priceNotIncludes
    );
    this.cloningQuotationEvent.emit(quotation);
  }

  createEmailForm(quotation: BookingQuotation) {
    this.bookingQuotationEmailForm = this.builder.group({
      subject: "Please find attached...",
      body: "",
      quotationId: quotation.id
    });
    this.displayEmailForm = true;
  }

  createContactRequestQuotationEmailForm(quotation: ContactRequestQuotation) {
    this.contactRequestQuotationEmailForm = this.builder.group({
      subject: "Please find attached...",
      body: "",
      quotationId: quotation.id
    });
    this.displayContactRequestQuotationEmailForm = true;
  }

  sendQuotationByEmail() {
    let message = new GmailMessage(
      [this.student.email],
      this.bookingQuotationEmailForm!.value.subject,
      this.bookingQuotationEmailForm!.value.body,
    );
    let quotationId = this.bookingQuotationEmailForm!.value.quotationId;
    this.httpService.sendBookingQuotationByGmail(message, quotationId, this.student.language.name)
      .subscribe(() => {
        this.toastService.addSuccess("Success", "Mail sent");
        this.displayEmailForm = false;
      }, error => this.toastService.addError("Error", error.message));
  }

  sendContactRequestQuotationByEmail() {
    let message = new GmailMessage(
      [this.student.email],
      this.contactRequestQuotationEmailForm!.value.subject,
      this.contactRequestQuotationEmailForm!.value.body,
    );
    let quotationId = this.contactRequestQuotationEmailForm!.value.quotationId;
    this.httpService.sendContactRequestQuotationByGmail(message, quotationId, this.student.language.name)
      .subscribe(() => {
        this.toastService.addSuccess("Success", "Mail sent");
        this.displayContactRequestQuotationEmailForm = false;
      }, error => this.toastService.addError("Error", error.message));
  }

  resetEmailForms() {
    this.bookingQuotationEmailForm = null;
    this.contactRequestQuotationEmailForm = null;
  }

  deleteQuotation(quotation: BookingQuotation) {
    this.confirmationService.confirm({
      message: "Are you sure that you want to delete this quotation?",
      accept: () => {
        this.httpService.deleteBookingQuotation(quotation.id!).subscribe(() => {
          this.initQuotationList();
        }, error => this.toastService.addError("Error", error.message));
      }
    });
  }

  initQuotationList() {
    this.httpService.getBookingQuotationsWithDetailByBookingId(this.bookingId).subscribe(res => {
      this.quotations = res;
    });
    this.httpService.getContactRequestQuotationsWithDetailByBookingId(this.bookingId).subscribe(res => {
      this.contactRequestQuotations = res;
    });
  }

  deleteContactRequestQuotation(quotation: ContactRequestQuotationWithDetail) {
    this.confirmationService.confirm({
      message: "Are you sure that you want to delete this quotation?",
      accept: () => {
        this.httpService.deleteContactRequestQuotation(quotation.id!).subscribe(() => {
          this.initQuotationList();
        }, error => this.toastService.addError("Error", error.message));
      }
    });
  }
}
